import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CreditCard, CheckCircle2, AlertTriangle, Clock, Sparkles, Heart, Zap } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const PILLS = {
  active:    { bg: "bg-emerald-500", label: "Active",    Icon: CheckCircle2 },
  sponsored: { bg: "bg-fuchsia-500", label: "Sponsored", Icon: Heart },
  trial:     { bg: "bg-sky-500",     label: "Free trial", Icon: Clock },
  lapsed:    { bg: "bg-destructive", label: "Lapsed",   Icon: AlertTriangle },
};

const STRIPE_PRICE_AUD = 5;

export default function SubscriptionPage() {
  const { subscription, refreshSubscription } = useAuth();
  const [busy, setBusy] = useState(false);
  const [stripeBusy, setStripeBusy] = useState(false);
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();

  // After Stripe redirect: poll status, credit on success.
  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid) return;
    let attempts = 0;
    let stopped = false;
    const poll = async () => {
      if (stopped || attempts >= 8) {
        if (!stopped) toast("Payment status check timed out — refresh in a few seconds.");
        return;
      }
      attempts += 1;
      try {
        const { data } = await api.get(`/billing/status/${sid}`);
        if (data.payment_status === "paid" && data.applied) {
          toast.success("Payment successful — your subscription is now active!");
          await refreshSubscription();
          // Strip session_id from URL
          params.delete("session_id"); setParams(params, { replace: true });
          navigate("/subscription", { replace: true });
          return;
        }
        if (data.status === "expired") {
          toast.error("Payment session expired. Please try again.");
          params.delete("session_id"); setParams(params, { replace: true });
          return;
        }
        setTimeout(poll, 2000);
      } catch (e) { /* keep polling */ setTimeout(poll, 2000); }
    };
    toast("Confirming your payment with Stripe…");
    poll();
    return () => { stopped = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!subscription) return <div className="p-8 text-muted-foreground">Loading…</div>;

  const setStatus = async (status) => {
    setBusy(true);
    try {
      await api.put("/me/subscription", { status });
      await refreshSubscription();
      toast.success(status === "active" ? "Subscription activated" :
                    status === "trial"  ? "Trial restarted" :
                    "Subscription set to lapsed");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const startCheckout = async () => {
    setStripeBusy(true);
    try {
      const { data } = await api.post("/billing/checkout", { origin_url: window.location.origin });
      if (data.url) window.location.href = data.url;
    } catch (e) { toast.error(formatApiError(e)); setStripeBusy(false); }
  };

  const meta = PILLS[subscription.status] || PILLS.lapsed;
  const PillIcon = meta.Icon;

  return (
    <div className="space-y-6 max-w-3xl" data-testid="subscription-page">
      <header>
        <h1 className="text-4xl font-display font-black tracking-tight flex items-center gap-3">
          <CreditCard className="w-8 h-8 text-coral" /> Subscription
        </h1>
        <p className="text-muted-foreground">
          Pay-only-if-you-edit. Approving timesheets & invoices is always free.
        </p>
      </header>

      {/* Status card */}
      <Card className="rounded-3xl p-6 border-2 space-y-4" data-testid="subscription-status-card">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wider text-white ${meta.bg}`}
                  data-testid="subscription-status-pill">
              <PillIcon className="w-3 h-3" /> {meta.label}
            </span>
            <h2 className="font-display font-black text-2xl mt-3">
              {subscription.status === "sponsored"
                ? "You're sponsored — enjoy the full app, on the house."
                : subscription.status === "active"
                ? "You can edit and send timesheets & invoices."
                : subscription.status === "trial"
                ? `${subscription.days_remaining} day${subscription.days_remaining === 1 ? "" : "s"} left on your free trial.`
                : "Your subscription has lapsed. Timesheets & invoices are read-only."}
            </h2>
            {subscription.trial_ends_at && subscription.status === "trial" && (
              <p className="text-sm text-muted-foreground mt-1">
                Trial ends on {new Date(subscription.trial_ends_at).toLocaleDateString()}
              </p>
            )}
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-3 pt-2">
          <FeatureRow ok>Approve / reject worker submissions</FeatureRow>
          <FeatureRow ok>Vital signs · Incidents · Kilometres</FeatureRow>
          <FeatureRow ok>NDIS budget tracking &amp; projections</FeatureRow>
          <FeatureRow ok>Profile, family gallery &amp; AI photos</FeatureRow>
          <FeatureRow ok={subscription.is_active}>Edit / submit / send timesheets</FeatureRow>
          <FeatureRow ok={subscription.is_active}>Edit / submit invoices</FeatureRow>
        </div>
      </Card>

      {/* Stripe Checkout — pay $5 AUD to extend by 30 days */}
      <Card className="rounded-3xl p-6 border-2 space-y-4 relative overflow-hidden" data-testid="subscription-checkout-card"
            style={{ background: "linear-gradient(135deg, hsl(var(--coral) / 0.08), hsl(var(--client) / 0.08))" }}>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h2 className="font-display font-extrabold text-2xl flex items-center gap-2">
              <Zap className="w-5 h-5 text-coral" /> Subscribe with Stripe
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              <span className="font-display font-black text-3xl text-foreground">A${STRIPE_PRICE_AUD}</span>
              <span className="ml-1">/ month — extends your active period by 30 days. Cancel any time, no auto-renewal.</span>
            </p>
          </div>
          <Button onClick={startCheckout} disabled={stripeBusy}
                  className="rounded-full font-bold gap-2 min-h-[56px] px-7"
                  style={{ background: "hsl(var(--coral))", color: "white" }}
                  data-testid="stripe-checkout-btn">
            <Zap className="w-4 h-4" /> {stripeBusy ? "Opening Stripe…" : "Subscribe with Stripe"}
          </Button>
        </div>
        <p className="text-xs text-muted-foreground border-t pt-3">
          You'll be redirected to Stripe's secure checkout. Card details never touch our servers. Test mode card: <code className="bg-muted px-1.5 py-0.5 rounded font-mono">4242 4242 4242 4242</code>, any future expiry, any CVC.
        </p>
      </Card>

      {/* Manual status toggles — kept for testing the lapsed flow / admin overrides */}
      <Card className="rounded-3xl p-6 border-2 space-y-4" data-testid="subscription-plans">
        <h2 className="font-display font-extrabold text-2xl flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-coral" /> Manual status (testing)
        </h2>
        <p className="text-sm text-muted-foreground">
          Flip your subscription status without going through Stripe — useful for testing the lapsed flow or restarting your trial.
        </p>
        <div className="grid sm:grid-cols-3 gap-3">
          <Button onClick={() => setStatus("active")} disabled={busy || subscription.status === "active"}
                  className="rounded-2xl min-h-[64px] font-bold gap-2"
                  style={{ background: "hsl(var(--client))" }}
                  data-testid="set-active-btn">
            <CheckCircle2 className="w-4 h-4" /> Mark Active
          </Button>
          <Button onClick={() => setStatus("trial")} disabled={busy}
                  variant="outline" className="rounded-2xl min-h-[64px] font-bold gap-2"
                  data-testid="restart-trial-btn">
            <Clock className="w-4 h-4" /> Restart 30-day trial
          </Button>
          <Button onClick={() => setStatus("lapsed")} disabled={busy || subscription.status === "lapsed"}
                  variant="outline" className="rounded-2xl min-h-[64px] font-bold gap-2 border-destructive/40 text-destructive"
                  data-testid="set-lapsed-btn">
            <AlertTriangle className="w-4 h-4" /> Set to lapsed
          </Button>
        </div>
        {subscription.is_admin && (
          <p className="text-xs text-muted-foreground border-t pt-3">
            <Heart className="w-3 h-3 inline mr-1 text-fuchsia-500" /> You're an admin. Visit
            {" "}<a href="/admin" className="font-bold underline">/admin</a> to grant sponsorships to other accounts.
          </p>
        )}
      </Card>
    </div>
  );
}

function FeatureRow({ children, ok }) {
  return (
    <div className="flex items-center gap-2 text-sm">
      {ok
        ? <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
        : <AlertTriangle className="w-4 h-4 text-destructive flex-shrink-0" />}
      <span className={ok ? "" : "text-muted-foreground line-through"}>{children}</span>
    </div>
  );
}
